/**
 * Los pasos del tutorial de bienvenida.
 *
 * Cada paso señala una zona de la pantalla por su atributo `data-tour`
 * (`[data-tour="buscador"]`…). Un paso cuya zona no existe o no se ve —un
 * permiso que esa persona no tiene, la barra lateral en el móvil— SE SALTA, de
 * modo que nadie ve «mira aquí» apuntando a la nada.
 */
export interface Paso {
  id: string;
  /** Valor de `data-tour` de la zona. Sin él, el paso va centrado. */
  zona?: string;
  titulo: string;
  texto: string;
  /** Dónde prefiere ponerse la tarjeta respecto de la zona. */
  lado?: 'derecha' | 'abajo' | 'arriba' | 'izquierda';
  /** Sólo en este tipo de pantalla. */
  en?: 'escritorio' | 'movil';
}

export const PASOS: Paso[] = [
  {
    id: 'bienvenida',
    titulo: 'Te damos la bienvenida a Obralink',
    texto:
      'Aquí vive cada obra de tu empresa: documentos, fotos, incidencias, presupuestos y más. ' +
      'Te enseñamos lo básico en un minuto. Puedes omitirlo cuando quieras.',
  },

  // ── Escritorio ────────────────────────────────────────────────────────────
  {
    id: 'menu',
    zona: 'menu-lateral',
    titulo: 'El menú',
    texto:
      'Desde aquí vas a cualquier sección. Sólo aparece lo que tu rol te permite usar: si algo ' +
      'no está, es que no te hace falta.',
    lado: 'derecha',
    en: 'escritorio',
  },
  {
    id: 'obras',
    zona: 'nav/obras',
    titulo: 'Obras',
    texto:
      'El centro de todo. Cada obra reúne su carpeta de documentos, fotos, incidencias, tareas y ' +
      'equipo. Empieza siempre por aquí.',
    lado: 'derecha',
    en: 'escritorio',
  },
  {
    id: 'documentos',
    zona: 'nav/documentos',
    titulo: 'Documentos',
    texto:
      'Todos los archivos de todas las obras, y un archivo general para lo que no es de ninguna ' +
      'obra. Se guardan con versiones: si alguien sube una nueva, la anterior no se pierde.',
    lado: 'derecha',
    en: 'escritorio',
  },
  {
    id: 'incidencias',
    zona: 'nav/incidencias',
    titulo: 'Incidencias',
    texto: 'Lo que va mal en obra, con foto, responsable y fecha. Se pueden abrir sin conexión.',
    lado: 'derecha',
    en: 'escritorio',
  },
  {
    id: 'almacenamiento',
    zona: 'almacenamiento',
    titulo: 'Tu espacio',
    texto: 'Cuánto espacio de la empresa llevas usado. Si se acerca al límite, avisa en color.',
    lado: 'derecha',
    en: 'escritorio',
  },

  // ── Móvil ─────────────────────────────────────────────────────────────────
  {
    id: 'menu-movil',
    zona: 'menu-movil',
    titulo: 'El menú',
    texto: 'Toca las tres rayas para ver todas las secciones.',
    lado: 'abajo',
    en: 'movil',
  },
  {
    id: 'navegacion-movil',
    zona: 'navegacion-movil',
    titulo: 'Lo de cada día',
    texto: 'Obras, fotos e incidencias, a un toque del pulgar. «Más» abre el resto del menú.',
    lado: 'arriba',
    en: 'movil',
  },

  // ── Ambos ─────────────────────────────────────────────────────────────────
  {
    id: 'buscador',
    zona: 'buscador',
    titulo: 'El buscador',
    texto:
      'Encuentra obras, archivos y personas escribiendo unas letras. Se abre desde cualquier ' +
      'pantalla con Ctrl + K (⌘ K en Mac).',
    lado: 'abajo',
  },
  {
    id: 'avisos',
    zona: 'avisos',
    titulo: 'Avisos',
    texto:
      'La campana te avisa de lo que te afecta: tareas asignadas, comentarios, documentos que ' +
      'caducan.',
    lado: 'abajo',
  },
  {
    id: 'ayuda',
    zona: 'ayuda-flotante',
    titulo: 'Ayuda en cada pantalla',
    texto:
      'Este botón está siempre aquí abajo. Según la pantalla donde estés, te enseña las dudas más ' +
      'habituales de esa zona, paso a paso.',
    lado: 'arriba',
  },
  {
    id: 'usuario',
    zona: 'usuario',
    titulo: 'Tu cuenta',
    texto:
      'Aquí cambias el tema claro u oscuro y cierras sesión. También puedes volver a ver este ' +
      'tutorial cuando quieras con «Ver el tutorial».',
    lado: 'abajo',
  },
  {
    id: 'final',
    titulo: 'Ya está',
    texto:
      'Eso es todo. Si te pierdes, el buscador te lleva a cualquier sitio, y en «Ayuda» (en el menú) ' +
      'tienes guías paso a paso para hacer lo de cada día.',
  },
];

/** ¿Está la zona en pantalla y con tamaño? Una zona oculta (display:none) mide 0. */
export function zonaVisible(zona: string): boolean {
  const el = document.querySelector<HTMLElement>(`[data-tour="${zona}"]`);
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

/** Los pasos que valen para esta pantalla ahora mismo. */
export function pasosAplicables(esMovil: boolean): Paso[] {
  return PASOS.filter((p) => {
    if (p.en === 'escritorio' && esMovil) return false;
    if (p.en === 'movil' && !esMovil) return false;
    return !p.zona || zonaVisible(p.zona);
  });
}
